import React from 'react';
import { InfoTip, StatCard } from '@/components/shared';
import type { NotificationMetricsData } from '../api/useNotificationMetrics';

interface SummaryCardsProps {
  data: NotificationMetricsData;
  loading: boolean;
}

function weekOverWeekChange(weekly: NotificationMetricsData['weekly'], field: keyof NotificationMetricsData['weekly'][0]): string | undefined {
  if (weekly.length < 2) return undefined;
  const current = Number(weekly[0][field]);
  const previous = Number(weekly[1][field]);
  if (previous === 0) return undefined;
  const pct = ((current - previous) / previous) * 100;
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}% vs prior week`;
}

function SummaryCards({ data, loading }: SummaryCardsProps): React.ReactElement {
  const { summary, weekly } = data;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <StatCard
        variant="dense"
        label="Sent"
        value={loading ? '—' : summary.total_sent.toLocaleString()}
        subtext={weekOverWeekChange(weekly, 'total_sent')}
        accent="violet"
        pending={loading}
        icon={
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
          </svg>
        }
      />
      <StatCard
        variant="dense"
        label="Opened"
        value={loading ? '—' : summary.total_opened.toLocaleString()}
        subtext={weekOverWeekChange(weekly, 'total_opened')}
        accent="blue"
        pending={loading}
        icon={
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 9v.906a2.25 2.25 0 01-1.183 1.981l-6.478 3.488M2.25 9v.906a2.25 2.25 0 001.183 1.981l6.478 3.488m8.839 2.51l-4.66-2.51m0 0l-1.023-.55a2.25 2.25 0 00-2.134 0l-1.022.55m0 0l-4.661 2.51m16.5 1.615a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V8.844a2.25 2.25 0 011.183-1.981l7.5-4.039a2.25 2.25 0 012.134 0l7.5 4.039a2.25 2.25 0 011.183 1.98V19.5z" />
          </svg>
        }
      />
      <StatCard
        variant="dense"
        label={<>Open Rate<InfoTip metric="b2b_open_rate" /></>}
        value={loading ? '—' : `${summary.open_rate}%`}
        subtext={weekOverWeekChange(weekly, 'open_rate')}
        accent="pink"
        pending={loading}
        icon={
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6a7.5 7.5 0 107.5 7.5h-7.5V6z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0013.5 3v7.5z" />
          </svg>
        }
      />
      <StatCard
        variant="dense"
        label={<>Attributed Visits<InfoTip metric="b2b_attributed_visits" /></>}
        value={loading ? '—' : summary.total_attributed_visits.toLocaleString()}
        subtext={weekOverWeekChange(weekly, 'attributed_visits')}
        accent="green"
        pending={loading}
        icon={
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
          </svg>
        }
      />
    </div>
  );
}

export default SummaryCards;
